import { Stack } from '@mui/joy';
import { ChangeEvent, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { SwitchInput } from 'core/components';
import { LabelPosition } from 'core/enums';
import { IAutocompleteOption } from 'core/types';
import { CorporateLocationAutocomplete } from 'corporate/elements';
import { CorporateLocationType } from 'corporate/enums';
import { ICorporateLocationAutocomplete } from 'corporate/types';
import { Direction } from 'theme/enums';

import { IInventoryFilterValue } from './types';

interface IProps {
  value: IInventoryFilterValue;
  onChange: (next: IInventoryFilterValue) => void;
  isAlternateVersionEnabled?: boolean;
  inline?: boolean;
  disabled?: boolean;
}

export const InventoryFilter = ({
  value,
  onChange,
  isAlternateVersionEnabled = false,
  inline = false,
  disabled = false,
}: IProps) => {
  const { t } = useTranslation();

  const handleOnToggleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({
      onlyWithInventory: event.target.checked,
      shippingLocation: null,
    });
  };

  const handleOnLocationChange = (_: SyntheticEvent, option: IAutocompleteOption) => {
    onChange({
      ...value,
      shippingLocation: (option as ICorporateLocationAutocomplete) ?? null,
    });
  };

  return (
    <Stack
      direction={inline ? Direction.ROW : Direction.COLUMN}
      sx={{
        gap: 1,
        alignItems: inline ? 'center' : 'flex-start',
        flexWrap: 'wrap',
      }}
    >
      <SwitchInput
        checked={value.onlyWithInventory}
        onChange={handleOnToggleChange}
        label={t('labelInStock')}
        labelPosition={LabelPosition.END}
        wrapperSx={{ height: inline ? '32px' : 'auto', alignItems: 'center' }}
        labelSx={inline ? { height: '100%' } : undefined}
        disabled={disabled}
      />

      {value.onlyWithInventory && (
        <CorporateLocationAutocomplete
          name="inventoryShippingLocation"
          placeholder={t('placeholderAnyLocation')}
          value={value.shippingLocation as IAutocompleteOption}
          onChange={handleOnLocationChange}
          type={CorporateLocationType.SHIPPING}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          disableClearable={false}
          wrapperStyle={{ width: inline ? 'auto' : '100%' }}
          disabled={disabled}
        />
      )}
    </Stack>
  );
};
